import { Component, Element, Event, EventEmitter, forceUpdate, h, Host, Listen, Prop, State } from "@stencil/core";
import { dynamicBulletsHandler } from "./dynamic-bullets-handler";
import { debounce } from "../../../utils/debounce";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-slider-pagination",
    styleUrl: "slider-pagination.scss",
    shadow: false
})
export class SliderPagination {
    private baseClass = "ux-slider-pagination";
    private bulletObserver: MutationObserver;

    @Element() paginationElement!: HTMLUxSliderPaginationElement;

    /** measures to set the position of the pagination in relation to the anchor element */
    @State() paginationOffsetTop = 0;
    @State() paginationOffsetLeft = 0;
    /** will be set to true if the slides are 10 or more, controls the styles */
    @State() isDynamic = false;
    /** true if slider on left position */
    @State() isBeginning = false;
    /** true if slider on right position */
    @State() isEnd = false;
    /** show Pagination only when there is more than one bullet */
    @State() showPagination = false;

    /** the unique id of the reference slider - this will be set automatically by the ux-slider */
    @Prop({ reflect: true }) readonly sliderId: string = "";
    /** if set, the navigation bullets will not have side arrows */
    @Prop() readonly withoutArrows: boolean = false;
    /** if set, the navigation will take the necessary styles to be the bottom navigation */
    @Prop() readonly bottomPagination: boolean = false;
    /** if set, the navigation will take the necessary styles to be the anchor navigation */
    @Prop() readonly anchorPagination: boolean = false;
    /** manages the pagination of custom slider types */
    @Prop() readonly customPagination: string = "";
    /** if set, the slider will show the slide label inside the slide pagination */
    @Prop() readonly label: boolean = false;
    /** if set, the slider will show the slide progress inside the slide pagination */
    @Prop() readonly progress: boolean = false;

    private bulletsContainer: HTMLDivElement;

    /** event to go to next slide */
    @Event({
        eventName: "uxSliderNextSlide",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    nextSlide!: EventEmitter;

    /** event to go to previous slide */
    @Event({
        eventName: "uxSliderPreviousSlide",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    previousSlide!: EventEmitter;

    private handleNextSlide() {
        this.nextSlide.emit(this.sliderId);
    }

    private handlePreviousSlide() {
        this.previousSlide.emit(this.sliderId);
    }

    @Listen("uxSliderUpdatePagination", { target: "document" })
    changeAnchorPosition(event: CustomEvent) {
        if (event.detail.id === this.sliderId) {
            this.handleAnchorPosition();
            const isDynamic = dynamicBulletsHandler(this.bulletsContainer);
            if (isDynamic !== this.isDynamic) {
                this.isDynamic = isDynamic;
            }
            if (event.detail.event === "checkSliderStartEnd") {
                this.isBeginning = event.detail.isBeginning;
                this.isEnd = event.detail.isEnd;
            }
        }
    }

    private handleAnchorPosition = () => {
        if (!this.anchorPagination) {
            return;
        }
        const slider = this.paginationElement.parentNode;
        // @ts-expect-error Check for typing
        const activeSlide: HTMLElement = slider.querySelector(`.ux-slider-slide-active`);
        if (activeSlide === null) {
            // @ts-expect-error Check for typing
            slider.querySelector(`.ux-slider-slide`);
        }
        if (activeSlide === null) {
            return;
        }
        // @ts-expect-error Check for typing
        const anchorElement: HTMLElement = activeSlide.querySelector(".ux-slider-inline-pagination-anchor");
        if (anchorElement === null) {
            return;
        }
        let additionalOffset = 0;
        const parentNode = anchorElement.parentNode as HTMLElement;
        if (anchorElement.parentNode !== activeSlide && parentNode.clientHeight > anchorElement.offsetTop) {
            additionalOffset = parentNode.offsetTop;
        }
        const offsetTop = anchorElement.offsetTop + Math.abs(additionalOffset);
        let additionalOffsetLeft = 0;
        if (anchorElement.parentNode !== activeSlide) {
            additionalOffsetLeft = (anchorElement.parentNode as HTMLElement).offsetLeft;
        }
        const offsetLeft = anchorElement.offsetLeft + additionalOffsetLeft + anchorElement.offsetWidth / 2;

        if (this.paginationOffsetTop === offsetTop && this.paginationOffsetLeft === offsetLeft) {
            return;
        }
        this.paginationOffsetTop = offsetTop;
        this.paginationOffsetLeft = offsetLeft;
    };

    componentWillLoad() {
        i18nProvider.subscribe(() => {
            forceUpdate(this);
        });
    }

    componentDidLoad() {
        this.handleAnchorPosition();
        this.bulletObserver = new MutationObserver(() => {
            this.showPagination = this.bulletsContainer.children.length > 1;
        });
        this.bulletObserver.observe(this.bulletsContainer, { childList: true });
    }

    private onResize = debounce(() => this.handleAnchorPosition(), 150);

    connectedCallback() {
        if (this.bottomPagination) {
            return;
        }
        window.addEventListener("resize", this.onResize);
    }

    disconnectedCallback() {
        if (this.bottomPagination) {
            return;
        }
        window.removeEventListener("resize", this.onResize);
        this.bulletObserver.disconnect();
    }

    render() {
        const {
            baseClass,
            bottomPagination,
            anchorPagination,
            withoutArrows,
            paginationOffsetTop,
            paginationOffsetLeft,
            isDynamic,
            label,
            progress
        } = this;
        const classes = [baseClass];

        if (bottomPagination) {
            classes.push(baseClass + "--bottom");
        } else if (anchorPagination) {
            classes.push(baseClass + "--anchor");
        }

        if (label) {
            classes.push(baseClass + "--label");
        }

        const anchorStyles = anchorPagination
            ? {
                  top: paginationOffsetTop > 0 ? `${paginationOffsetTop}px` : "100%",
                  left: paginationOffsetLeft > 0 ? `${paginationOffsetLeft}px` : "50%"
              }
            : null;
        const display = this.showPagination ? undefined : "none";

        const { t } = i18nProvider;

        return (
            <Host class={classes.join(" ")} style={{ ...anchorStyles, display }}>
                {!withoutArrows && (
                    <ux-icon
                        name="chevron-right"
                        weight="normal"
                        class={`${baseClass}__navigation-arrow ${baseClass}__navigation-arrow--prev`}
                        onClick={this.handlePreviousSlide.bind(this)}
                        role="button"
                        aria-label={t("slider.prevButton.label")}
                        {...(this.isBeginning && { "aria-disabled": "true" })}
                    />
                )}
                <div
                    class={{
                        [`${baseClass}__container`]: true,
                        [`${baseClass}__container--dynamic`]: isDynamic,
                        [`${baseClass}__container--label`]: label,
                        [`${baseClass}__container--progress`]: progress
                    }}
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.bulletsContainer = el)}
                ></div>
                {!withoutArrows && (
                    <ux-icon
                        name="chevron-right"
                        weight="normal"
                        class={`${baseClass}__navigation-arrow ${baseClass}__navigation-arrow--next`}
                        onClick={this.handleNextSlide.bind(this)}
                        role="button"
                        aria-label={t("slider.nextButton.label")}
                        {...(this.isEnd && { "aria-disabled": "true" })}
                    />
                )}
            </Host>
        );
    }
}
